import * as fns from 'date-fns/fp';
import { function as F, option as O } from 'fp-ts';
import { Eq, eqString } from 'fp-ts/Eq';
import { fromCompare } from 'fp-ts/Ord';
import { Brand } from './brand';
import { zeroPadStart } from './time';

// yyyy-MM-dd
export type AppDate = Brand<string, 'AppDate'>;

export function isValidAppDate(value?: string | null): value is AppDate {
  return F.pipe(
    value,
    O.fromNullable,
    O.map(F.flow(fns.parse(new Date())('yyyy-MM-dd'), fns.isValid)),
    O.fold(() => false, F.identity),
  );
}

export function parseAppDate(value: AppDate): Date {
  return F.pipe(value, fns.parse(new Date())('yyyy-MM-dd'));
}

export function toAppDate(value: string | Date, utcDate = false): AppDate {
  if (value instanceof Date) {
    if (utcDate) {
      return [value.getUTCFullYear(), value.getUTCMonth() + 1, value.getUTCDate()]
        .map(zeroPadStart(2))
        .join('-') as AppDate;
    }

    return [value.getFullYear(), value.getMonth() + 1, value.getDate()]
      .map(zeroPadStart(2))
      .join('-') as AppDate;
  }

  if (!isValidAppDate(value)) {
    throw new Error('Could not parse app date.');
  }

  return value;
}

export const eqAppDate = eqString as Eq<AppDate>;

export const ordAppDate = fromCompare((a?: AppDate, b?: AppDate) => {
  if (a === b) {
    return 0;
  }

  if (!a) {
    return -1;
  }

  if (!b) {
    return 1;
  }

  const aDate = parseAppDate(a);
  const bDate = parseAppDate(b);

  return aDate > bDate ? 1 : -1;
});

export function calcBusinessDays(startDate: Date, endDate: Date) {
  // eslint-disable-next-line no-param-reassign
  startDate = new Date(startDate.getTime());
  // eslint-disable-next-line no-param-reassign
  endDate = new Date(endDate.getTime());

  if (endDate < startDate) {
    return 0;
  }

  const millisecondsPerDay = 86400 * 1000; // Day in milliseconds
  startDate.setHours(0, 0, 0, 1); // Start just after midnight
  endDate.setHours(23, 59, 59, 999); // End just before midnight
  const diff = endDate.getTime() - startDate.getTime(); // Milliseconds between datetime objects
  let days = Math.ceil(diff / millisecondsPerDay);

  // Subtract two weekend days for every week in between
  const weeks = Math.floor(days / 7);
  days = days - weeks * 2;

  // Handle special cases
  const startDay = startDate.getDay();
  const endDay = endDate.getDay();

  // Remove weekend not previously removed.
  if (startDay - endDay > 1) {
    days = days - 2;
  }

  // Remove start day if span starts on Sunday but ends before Saturday
  if (startDay === 0 && endDay !== 6) {
    days = days - 1;
  }

  // Remove end day if span ends on Saturday but starts after Sunday
  if (endDay === 6 && startDay !== 0) {
    days = days - 1;
  }

  return days;
}
